import { useState } from "react";
import { Link } from "react-router-dom";
import SEO from "../../../components/SEO";
import img99 from "../../../assets/img99.jpeg";
import ebookImg from "../../../assets/ebookImg.webp";
import img6 from "../../../assets/img6.jpg";
import img7 from "../../../assets/img7.webp";
import img8 from "../../../assets/img8.webp";
import img9 from "../../../assets/img9.webp";
import { CheckCircle, Plus, Minus } from "lucide-react";
import img10 from "../../../assets/img10.jpg";
import BlogSection from "../../../components/BlogSection";

const LifeInsurance = () => {
  const [activeIndex, setActiveIndex] = useState(null);
  const toggleFAQ = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  const siteContainer = "mx-auto w-full max-w-[1380px]";

  const faqs = [
    {
      question: "Comment puis-je obtenir une soumission d'assurance vie au Canada ?",
      answer:
        "Vous pouvez facilement obtenir une soumission d'assurance vie au Canada en vous rendant sur einsured.ca et en remplissant leur formulaire en ligne. Leur équipe expérimentée vous fournira instantanément plusieurs soumissions d'assurance vie au Canada à comparer et à choisir.",
    },
    {
      question: "Quels types d'assurance vie sont accessibles à Brampton ?",
      answer:
        "À Brampton, vous avez accès à différentes options d'assurance vie, notamment l'assurance temporaire, l'assurance vie entière, l'assurance vie universelle et les régimes sans examen médical. Chaque type répond à des besoins variés, que vous recherchiez une couverture temporaire ou une protection permanente avec des composantes d'investissement.",
    },
    {
      question: "L'assurance vie sans examen médical est-elle accessible à Brampton ?",
      answer:
        "Oui, l'assurance vie sans examen médical est accessible à Brampton par l'intermédiaire de fournisseurs comme einsured.ca. Ces régimes conviennent parfaitement aux personnes qui souhaitent obtenir une couverture rapide sans passer d'examens médicaux traditionnels.",
    },
    {
      question: "Comment choisir le bon régime d'assurance vie à Brampton ?",
      answer:
        "Choisir le bon régime implique d'évaluer vos objectifs financiers, vos besoins de couverture et votre budget. Consulter les conseillers autorisés d'einsured.ca peut vous aider à comparer les options et à sélectionner un régime qui correspond à vos besoins particuliers.",
    },
    {
      question: "Puis-je obtenir une assurance vie abordable à Brampton ?",
      answer:
        "Absolument ! Les résidents de Brampton peuvent trouver des options d'assurance vie abordables en comparant les offres de différents fournisseurs. einsured.ca propose des tarifs compétitifs et différents types de régimes pour s'adapter à tous les budgets et à tous les besoins.",
    },
  ];

  return (
    <>
      <SEO
        title="Assurance vie au Canada | Couverture flexible – einsured.ca"
        description="Explorez l’assurance-vie au Canada avec einsured.ca. Comparez les régimes temporaires et permanents pour une couverture adaptée à vos besoins et à votre budget."
        keywords="Assurance vie au Canada, assurance vie temporaire, assurance vie entière, assurance vie universelle Canada"
      />

      {/* Hero Section */}
      <section
        className="relative min-h-[60vh] sm:min-h-[70vh] md:min-h-[80vh] bg-cover bg-center flex items-center justify-center md:justify-start px-4 sm:px-6 md:px-20"
        style={{ backgroundImage: `url(${img99})` }}
        role="img"
        aria-label="Bannière principale assurance vie Canada"
      >
        <div className="absolute inset-0 bg-black/40"></div>

        <div
          className={`relative z-10 ${siteContainer} px-4 sm:px-8 md:px-24 text-white text-center md:text-left`}
        >
          <div className="max-w-2xl">
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-3 sm:mb-4">
              Assurance vie Canada
            </h1>
            <p className="text-base sm:text-lg md:text-xl mb-6 sm:mb-8 font-medium">
              Des solutions d'assurance vie personnalisées adaptées aux besoins de votre famille
            </p>
            <a
              href="https://book.einsured.ca/widget/bookings/ei-team"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block w-full sm:w-auto px-8 py-3 border-2 border-sky-400 bg-sky-400 text-white rounded-full text-base sm:text-lg font-semibold hover:bg-sky-600 transition-all duration-300"
            >
              Commencer
            </a>
          </div>
        </div>
      </section>

      {/* Why Life Insurance */}
      <section className="py-16 sm:py-18 px-4 sm:px-6 md:px-24 bg-white text-gray-800">
        <div className={`${siteContainer}`}>
          <h2 className="text-2xl sm:text-3xl text-[#49628b] md:text-4xl font-bold text-center mb-6 sm:mb-8">
            Pourquoi l'assurance vie est-elle essentielle pour les particuliers ?
          </h2>
          <div className="max-w-4xl mx-auto text-base sm:text-lg leading-relaxed space-y-5">
            <p>
              Réfléchissez aux défis potentiels que vos proches pourraient rencontrer en cas de décès prématuré. Ils pourraient avoir du mal à maintenir leur logement, à payer leurs factures et à subvenir aux besoins de leurs enfants. Cependant, grâce à une{" "}
              <span className="text-[#20a7df] font-semibold">
                assurance vie de qualité
              </span>{" "}
              recommandée par{" "}
              <Link to="/" className="text-[#20a7df] font-semibold">
                einsured.ca
              </Link>
              , vous pouvez vous assurer que votre famille n'aura jamais à faire face à des difficultés financières résultant de la perte du principal soutien de famille.
            </p>
            <p>
              Ils auront accès aux fonds nécessaires pour mener une vie confortable et épanouissante à long terme, notamment pour rembourser toutes les dettes ou obligations en cours. Non seulement l'{" "}
              <span className="text-[#20a7df] font-semibold">
                assurance vie
              </span>{" "}
              vous offre la tranquillité d'esprit de protéger ceux qui vous sont chers, mais c'est aussi un investissement judicieux pour votre avenir.
            </p>
            <p>
              Chez{" "}
              <Link to="/" className="text-[#20a7df] font-semibold">
                einsured.ca
              </Link>
              , nous pouvons personnaliser votre police pour répondre à vos besoins spécifiques à un prix raisonnable, rendant le processus sans stress. N'hésitez pas à franchir cette étape cruciale pour assurer un avenir meilleur à vos proches.
            </p>
          </div>
        </div>
      </section>

      {/* Ebook Promo */}
      <section className="py-16 sm:py-20 bg-gray-100 flex flex-col md:flex-row items-center justify-center px-4 sm:px-6 md:px-24 gap-8 sm:gap-10">
        <div
          className={`${siteContainer} flex flex-col md:flex-row items-center justify-center gap-8 sm:gap-10`}
        >
          <div className="flex-shrink-0">
            <img
              src={ebookImg}
              alt="Guide simple sur l'assurance vie"
              className="w-60 sm:w-72 md:w-80 rounded-lg shadow-md"
            />
          </div>
          <div className="max-w-md text-center md:text-left">
            <h2 className="text-2xl sm:text-3xl font-bold mb-4 sm:mb-6 text-[#49628b]">
              Livre numérique – Guide simple sur l'assurance vie
            </h2>
            <ul className="space-y-2.5 sm:space-y-3 text-base sm:text-lg">
              <li>✅ Prenez le contrôle</li>
              <li>✅ Couverture complète</li>
              <li>✅ Planification financière stratégique</li>
              <li>✅ Accès GRATUIT à la sagesse</li>
              <li>✅ Conseils d'experts</li>
            </ul>
            <Link
              to="/fr/guide-assurance-vie"
              className="inline-block mt-6 sm:mt-8 px-8 py-3 bg-sky-500 text-white text-base sm:text-lg font-semibold rounded-full hover:bg-sky-600 transition-all w-full sm:w-auto"
            >
              Accès gratuit
            </Link>
          </div>
        </div>
      </section>

      {/* Life Insurance Terminology */}
      <section
        className="relative py-20 sm:py-24 md:py-28 px-4 sm:px-6 md:px-24 bg-cover bg-center lg:bg-fixed"
        style={{ backgroundImage: `url(${img6})` }}
        role="img"
        aria-label="Image de fond terminologie de l'assurance vie"
      >
        <div className="absolute inset-0 bg-sky-900/70"></div>

        <div
          className={`relative z-10 ${siteContainer} text-center text-white`}
        >
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 sm:mb-6 text-white">
            Terminologie de l'assurance vie
          </h2>

          <p className="max-w-4xl mx-auto text-base sm:text-lg leading-relaxed mb-8 sm:mb-12 text-gray-100">
            L'assurance vie est un contrat financier entre un particulier (le titulaire de la police) et une compagnie d'assurance. En échange de paiements de primes réguliers, la compagnie d'assurance verse une prestation de décès — un paiement forfaitaire — aux bénéficiaires désignés au décès de la personne assurée. L'objectif principal de l'assurance vie est d'offrir une protection financière et un soutien à la famille survivante ou aux bénéficiaires après le décès du titulaire de la police.
          </p>

          <h3 className="text-lg sm:text-2xl font-semibold mb-8 sm:mb-10 text-[#20a7df]">
            Voici quelques composantes et caractéristiques clés de l'assurance vie :
          </h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            <div className="bg-white/95 rounded-2xl p-6 shadow-lg hover:shadow-xl transition text-gray-800">
              <h4 className="text-lg sm:text-xl font-semibold mb-3 text-[#49628b]">
                Primes :
              </h4>
              <p>
                Les titulaires de police versent des primes régulières à la compagnie d'assurance pour maintenir la police en vigueur. Les primes peuvent généralement être payées mensuellement, trimestriellement, semestriellement ou annuellement.
              </p>
            </div>

            <div className="bg-white/95 rounded-2xl p-6 shadow-lg hover:shadow-xl transition text-gray-800">
              <h4 className="text-lg sm:text-xl font-semibold mb-3 text-[#49628b]">
                Prestation de décès :
              </h4>
              <p>
                La prestation de décès est la somme d'argent que la compagnie d'assurance verse aux bénéficiaires au décès de la personne assurée. Les bénéficiaires sont généralement désignés par le titulaire de la police et peuvent être des membres de la famille, des amis ou toute autre personne ou entité choisie par le titulaire.
              </p>
            </div>

            <div className="bg-white/95 rounded-2xl p-6 shadow-lg hover:shadow-xl transition text-gray-800">
              <h4 className="text-lg sm:text-xl font-semibold mb-3 text-[#49628b]">
                Bénéficiaires :
              </h4>
              <p>
                Le titulaire de la police désigne des bénéficiaires qui recevront la prestation de décès. Les bénéficiaires peuvent être des particuliers, des fiducies ou même des organismes de bienfaisance.
              </p>
            </div>

            <div className="bg-white/95 rounded-2xl p-6 shadow-lg hover:shadow-xl transition text-gray-800">
              <h4 className="text-lg sm:text-xl font-semibold mb-3 text-[#49628b]">
                Souscription :
              </h4>
              <p>
                Le processus d'évaluation de la santé, du mode de vie et d'autres facteurs d'une personne pour déterminer le risque de l'assurer. Les résultats du processus de souscription peuvent influencer le montant de la prime.
              </p>
            </div>

            <div className="bg-white/95 rounded-2xl p-6 shadow-lg hover:shadow-xl transition text-gray-800 sm:col-span-2 lg:col-span-4">
              <h4 className="text-lg sm:text-xl font-semibold mb-3 text-[#49628b]">
                Avenants :
              </h4>
              <p>
                Des caractéristiques ou options supplémentaires pouvant être ajoutées à une police d'assurance vie pour personnaliser la couverture. Les avenants courants comprennent les avenants de prestations de décès accéléré, qui permettent au titulaire de la police d'accéder à une partie de la prestation de décès en cas de diagnostic de maladie en phase terminale.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Types of Life Insurance */}
      <section className="py-16 sm:py-20 px-4 sm:px-6 md:px-24 bg-white text-gray-800">
        <div className={`${siteContainer}`}>
          <h2 className="text-3xl sm:text-4xl font-bold text-center text-[#49628b] mb-6">
            Types d'assurance vie
          </h2>
          <p className="max-w-4xl mx-auto text-base sm:text-lg text-center mb-4 sm:mb-6 text-gray-700">
            L'assurance vie répond à divers objectifs, notamment assurer la sécurité financière des personnes à charge, couvrir les dettes et dépenses en cours, financer les études des enfants et garantir un héritage à ceux qu'on aime. Le{" "}
            <span className="text-[#20a7df] font-semibold">
              type d'assurance vie
            </span>{" "}
            et le montant de la couverture dépendent des besoins individuels, des objectifs financiers et des circonstances.
          </p>
          <p className="max-w-4xl mx-auto text-base sm:text-lg text-center mb-12 sm:mb-16 text-gray-700">
            <span className="text-[#20a7df] font-semibold">
              Les régimes d'assurance vie au Canada
            </span>{" "}
            existent en différents types, chacun conçu pour répondre à des besoins et des préférences différents. Comprendre ces types peut vous aider à prendre des décisions éclairées en fonction de vos objectifs financiers et de votre situation.
          </p>

          {/* Term Life Insurance */}
          <div className="flex flex-col md:flex-row items-center gap-6 sm:gap-10 mb-14 sm:mb-20">
            <div className="md:w-1/2 w-full">
              <img
                loading="lazy"
                src={img7}
                alt="Assurance vie temporaire"
                className="rounded-2xl shadow-md w-full"
              />
            </div>
            <div className="md:w-1/2 w-full">
              <Link to="/fr/assurance-vie-temporaire" className="block">
                <h3 className="text-2xl sm:text-3xl font-bold text-[#49628b] mb-4 sm:mb-6 hover:underline cursor-pointer">
                  Assurance vie temporaire
                </h3>
              </Link>
              <ul className="space-y-3 sm:space-y-4 text-base sm:text-lg">
                <li className="flex items-start">
                  <CheckCircle
                    aria-hidden="true"
                    className="text-sky-500 w-6 h-6 mr-3 mt-1"
                  />
                  <span>
                    <strong>Durée de couverture :</strong> Offre une couverture pour une période déterminée (p. ex., 10, 20 ou 30 ans).
                  </span>
                </li>
                <li className="flex items-start">
                  <CheckCircle
                    aria-hidden="true"
                    className="text-sky-500 w-6 h-6 mr-3 mt-1"
                  />
                  <span>
                    <strong>Primes :</strong> Généralement plus abordables que l'assurance vie permanente.
                  </span>
                </li>
                <li className="flex items-start">
                  <CheckCircle
                    aria-hidden="true"
                    className="text-sky-500 w-6 h-6 mr-3 mt-1"
                  />
                  <span>
                    <strong>Prestation de décès :</strong> Versée si l'assuré décède pendant la durée de la police.
                  </span>
                </li>
                <li className="flex items-start">
                  <CheckCircle
                    aria-hidden="true"
                    className="text-sky-500 w-6 h-6 mr-3 mt-1"
                  />
                  <span>
                    <strong>Renouvellement :</strong> Généralement renouvelable, mais les primes peuvent augmenter au renouvellement.
                  </span>
                </li>
              </ul>

              <a
                href="https://compulife.org/website/655/quoter.html"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-6 sm:mt-8 inline-block bg-sky-500 text-white px-8 py-3 rounded-full font-semibold text-base sm:text-lg hover:bg-sky-600 transition w-full sm:w-auto text-center"
              >
                Obtenir une soumission
              </a>
            </div>
          </div>

          {/* Whole Life Insurance */}
          <div className="flex flex-col md:flex-row-reverse items-center gap-6 sm:gap-10 mb-14 sm:mb-20">
            <div className="md:w-1/2 w-full">
              <img
                loading="lazy"
                src={img8}
                alt="Assurance vie entière"
                className="rounded-2xl shadow-md w-full"
              />
            </div>
            <div className="md:w-1/2 w-full">
              <Link to="/fr/assurance-vie-entiere" className="block">
                <h3 className="text-2xl sm:text-3xl font-bold text-[#49628b] mb-4 sm:mb-6 hover:underline cursor-pointer">
                  Assurance vie entière
                </h3>
              </Link>
              <ul className="space-y-3 sm:space-y-4 text-base sm:text-lg">
                <li className="flex items-start">
                  <CheckCircle
                    aria-hidden="true"
                    className="text-sky-500 w-6 h-6 mr-3 mt-1"
                  />
                  <span>
                    <strong>Durée de couverture :</strong> Offre une couverture pour toute la vie de l'assuré.
                  </span>
                </li>
                <li className="flex items-start">
                  <CheckCircle
                    aria-hidden="true"
                    className="text-sky-500 w-6 h-6 mr-3 mt-1"
                  />
                  <span>
                    <strong>Primes :</strong> Plus élevées que l'assurance vie temporaire, mais elles restent stables.
                  </span>
                </li>
                <li className="flex items-start">
                  <CheckCircle
                    aria-hidden="true"
                    className="text-sky-500 w-6 h-6 mr-3 mt-1"
                  />
                  <span>
                    <strong>Valeur de rachat :</strong> S'accumule avec le temps et peut être empruntée ou retirée.
                  </span>
                </li>
                <li className="flex items-start">
                  <CheckCircle
                    aria-hidden="true"
                    className="text-sky-500 w-6 h-6 mr-3 mt-1"
                  />
                  <span>
                    <strong>Prestation de décès :</strong> Versée aux bénéficiaires au décès de l'assuré.
                  </span>
                </li>
              </ul>

              <a
                href="https://compulife.org/website/655/quoter.html"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-6 sm:mt-8 inline-block bg-sky-500 text-white px-8 py-3 rounded-full font-semibold text-base sm:text-lg hover:bg-sky-600 transition w-full sm:w-auto text-center"
              >
                Obtenir une soumission
              </a>
            </div>
          </div>

          {/* Universal Life Insurance */}
          <div className="flex flex-col md:flex-row items-center gap-6 sm:gap-10">
            <div className="md:w-1/2 w-full">
              <img
                loading="lazy"
                src={img9}
                alt="Assurance vie universelle"
                className="rounded-2xl shadow-md w-full"
              />
            </div>
            <div className="md:w-1/2 w-full">
              <Link to="/fr/assurance-vie-universelle" className="block">
                <h3 className="text-2xl sm:text-3xl font-bold text-[#49628b] mb-4 sm:mb-6 hover:underline cursor-pointer">
                  Assurance vie universelle
                </h3>
              </Link>
              <ul className="space-y-3 sm:space-y-4 text-base sm:text-lg">
                <li className="flex items-start">
                  <CheckCircle
                    aria-hidden="true"
                    className="text-sky-500 w-6 h-6 mr-3 mt-1"
                  />
                  <span>
                    <strong>Flexibilité :</strong> Offre de la flexibilité dans les paiements de primes et les prestations de décès.
                  </span>
                </li>
                <li className="flex items-start">
                  <CheckCircle
                    aria-hidden="true"
                    className="text-sky-500 w-6 h-6 mr-3 mt-1"
                  />
                  <span>
                    <strong>Primes :</strong> Ajustables dans certaines limites.
                  </span>
                </li>
                <li className="flex items-start">
                  <CheckCircle
                    aria-hidden="true"
                    className="text-sky-500 w-6 h-6 mr-3 mt-1"
                  />
                  <span>
                    <strong>Prestation de décès :</strong> Versée aux bénéficiaires et peut être ajustable selon les conditions de la police.
                  </span>
                </li>
                <li className="flex items-start">
                  <CheckCircle
                    aria-hidden="true"
                    className="text-sky-500 w-6 h-6 mr-3 mt-1"
                  />
                  <span>
                    <strong>Valeur de rachat :</strong> Comporte une composante d'accumulation de valeur de rachat avec un potentiel de croissance par investissement.
                  </span>
                </li>
              </ul>
              <a
                href="https://compulife.org/website/655/quoter.html"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-6 sm:mt-8 inline-block bg-sky-500 text-white px-8 py-3 rounded-full font-semibold text-base sm:text-lg hover:bg-sky-600 transition w-full sm:w-auto text-center"
              >
                Obtenir une soumission
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Life Insurance and Different Age Groups */}
      <section
        className="relative py-20 sm:py-24 md:py-28 px-4 sm:px-6 md:px-24 bg-cover bg-center bg-fixed text-white"
        style={{ backgroundImage: `url(${img10})` }}
        role="img"
        aria-label="Image de fond assurance vie et différents groupes d'âge"
      >
        <div className="absolute inset-0 bg-sky-900 bg-opacity-75" />
        <div className={`relative z-10 ${siteContainer}`}>
          <h2 className="text-3xl sm:text-4xl font-bold text-center text-white mb-6">
            L'assurance vie et les différents groupes d'âge
          </h2>
          <p className="max-w-4xl mx-auto text-base sm:text-lg text-center mb-6 sm:mb-8 text-[#d5ebff]">
            <span className="text-[#20a7df] font-semibold">
              L'assurance vie
            </span>{" "}
            est importante pour les personnes de tous les groupes d'âge, et son importance évolue avec les différentes étapes de la vie. Voici un aperçu de pourquoi l'assurance vie est essentielle pour différents groupes d'âge.
          </p>

          {/* Card Grid 1 */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 mb-10 sm:mb-12">
            {[
              {
                title: "Jeunes adultes (20 et 30 ans) :",
                points: [
                  {
                    label: "Protection financière pour les personnes à charge",
                    desc: "Si les jeunes adultes ont des personnes à charge, comme un conjoint ou des enfants, l'assurance vie offre une protection financière en cas de décès prématuré. Elle aide à garantir que les personnes à charge puissent maintenir leur niveau de vie et couvrir des dépenses comme les paiements hypothécaires, les études et les frais de subsistance quotidiens.",
                  },
                  {
                    label: "Bloquer des primes plus basses",
                    desc: "Souscrire une assurance vie à un jeune âge entraîne souvent des coûts de primes moins élevés. Souscrire une police tôt peut être un moyen rentable d'obtenir une couverture à long terme.",
                  },
                ],
              },
              {
                title: "Couples et nouveaux parents :",
                points: [
                  {
                    label: "Protéger les familles en expansion",
                    desc: "L'assurance vie devient encore plus critique pour les couples qui fondent une famille. Elle aide à protéger les conjoints et les enfants, assurant la stabilité financière et couvrant les besoins futurs tels que les frais d'études et les paiements hypothécaires.",
                  },
                  {
                    label: "Remplacement du revenu",
                    desc: "En cas de décès d'un parent, l'assurance vie peut remplacer le revenu perdu, aidant le conjoint survivant à maintenir le mode de vie de sa famille et à honorer ses obligations financières.",
                  },
                ],
              },
              {
                title: "Milieu de vie (40 et 50 ans) :",
                points: [
                  {
                    label: "Planification successorale",
                    desc: "L'assurance vie devient un outil de planification successorale, aidant à minimiser les implications fiscales potentielles et à assurer un transfert fluide des actifs aux héritiers.",
                  },
                  {
                    label: "Remboursement des dettes",
                    desc: "Les personnes dans la quarantaine et la cinquantaine peuvent avoir accumulé diverses dettes, telles que des hypothèques et des prêts en cours. L'assurance vie peut être utilisée pour couvrir ces dettes, évitant que le fardeau ne passe aux membres de la famille survivants.",
                  },
                ],
              },
            ].map((group, idx) => (
              <div
                key={idx}
                className="p-6 sm:p-8 rounded-2xl shadow-lg bg-white/10 backdrop-blur-md border border-white/10 hover:bg-white/20 transition text-[#e7f4ff]"
              >
                <h3 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4 text-white">
                  {group.title}
                </h3>
                <ul className="space-y-4 text-base sm:text-lg">
                  {group.points.map((pt, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <CheckCircle
                        aria-hidden="true"
                        className="h-6 w-6 text-sky-500 mt-1 shrink-0"
                        strokeWidth={2.5}
                        fill="none"
                      />
                      <span>
                        <strong className="text-sky-300">{pt.label} :</strong>{" "}
                        {pt.desc}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Card Grid 2 */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
            {[
              {
                title: "Enfants qui ont quitté le foyer (50 et 60 ans) :",
                points: [
                  {
                    label: "Préservation du patrimoine",
                    desc: "L'assurance vie peut servir de véhicule pour préserver le patrimoine et laisser un héritage financier à ses héritiers ou à des causes caritatives.",
                  },
                  {
                    label: "Couverture des frais finaux",
                    desc: "En vieillissant, le besoin d'assurance vie pour couvrir les frais finaux, comme les frais funéraires et les factures médicales en cours, devient plus important.",
                  },
                ],
              },
              {
                title: "Retraités (60 ans et plus) :",
                points: [
                  {
                    label: "Soutien au conjoint survivant",
                    desc: "L'assurance vie peut fournir un soutien financier au conjoint survivant, lui permettant de maintenir sa qualité de vie et de couvrir ses dépenses.",
                  },
                  {
                    label: "Planification successorale",
                    desc: "Pour ceux qui ont accumulé des actifs importants, l'assurance vie peut faire partie d'une stratégie globale de planification successorale, leur permettant de transmettre leur patrimoine aux générations futures ou à des organisations caritatives.",
                  },
                ],
              },
            ].map((group, idx) => (
              <div
                key={idx}
                className="p-6 sm:p-8 rounded-2xl shadow-lg bg-white/10 backdrop-blur-md border border-white/10 hover:bg-white/20 transition text-[#e7f4ff]"
              >
                <h3 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4 text-white">
                  {group.title}
                </h3>
                <ul className="space-y-4 text-base sm:text-lg">
                  {group.points.map((pt, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <CheckCircle
                        aria-hidden="true"
                        className="h-6 w-6 text-sky-500 mt-1 shrink-0"
                        strokeWidth={2.5}
                        fill="none"
                      />
                      <span>
                        <strong className="text-sky-300">{pt.label} :</strong>{" "}
                        {pt.desc}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <p className="max-w-5xl mx-auto text-base sm:text-lg text-center mt-12 text-[#d5ebff] leading-relaxed px-1">
            Quel que soit votre âge, l'assurance vie offre une tranquillité d'esprit en fournissant un filet de sécurité financière pour vos proches. Il ne s'agit pas seulement de remplacer un revenu perdu, mais aussi de s'assurer que les objectifs et les obligations financières sont atteints, quelles que soient les incertitudes de la vie. Le type et le montant spécifiques de la couverture varieront en fonction des circonstances et des objectifs individuels à chaque étape de la vie.
          </p>

          <div className="flex justify-center mt-8 sm:mt-10">
            <a
              href="https://compulife.org/website/655/quoter.html"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-sky-400 hover:bg-sky-500 text-white px-8 sm:px-10 py-3 rounded-full text-base sm:text-lg font-semibold shadow-md transition w-full sm:w-auto text-center inline-block"
            >
              Trouver le prix le plus bas
            </a>
          </div>
        </div>
      </section>

      {/* How to Make Life Insurance Affordable */}
      <section className="py-16 sm:py-20 px-4 sm:px-6 md:px-24 bg-gray-50 text-gray-800">
        <div className={`${siteContainer}`}>
          <div className="max-w-5xl mx-auto text-center mb-8 sm:mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#49628b] mb-3 sm:mb-4">
              Comment rendre l'assurance vie abordable
            </h2>
            <p className="text-base sm:text-lg text-gray-700">
              Obtenir une assurance vie abordable implique de comprendre vos besoins, de rechercher les options disponibles et de prendre des décisions éclairées.
            </p>
          </div>

          <div className="max-w-5xl mx-auto">
            <h3 className="text-xl sm:text-2xl font-bold text-[#49628b] mb-6 sm:mb-10 text-center">
              Voici un guide étape par étape pour vous aider à obtenir une assurance vie abordable :
            </h3>

            <ul className="space-y-6 sm:space-y-7 text-base sm:text-lg leading-relaxed">
              {[
                {
                  title: "Évaluez vos besoins",
                  desc: (
                    <>
                      Déterminez l'objectif de votre couverture d'assurance vie. Cherchez-vous à assurer un soutien financier à votre famille, à couvrir des dettes spécifiques ou à garantir un héritage à vos proches ? Comprendre vos besoins vous aidera à choisir le bon type de couverture et une bonne police d'assurance vie.
                    </>
                  ),
                },
                {
                  title: "Comprendre les différents types d'assurance vie",
                  desc: (
                    <>
                      Explorez les différents types d'assurance vie, comme l'assurance vie temporaire,{" "}
                      <Link
                        to="#"
                        className="text-[#20a7df] font-semibold hover:underline"
                      >
                        l'assurance vie entière,
                      </Link>{" "}
                      et{" "}
                      <Link
                        to="#"
                        className="text-[#20a7df] font-semibold hover:underline"
                      >
                        l'assurance vie universelle.
                      </Link>{" "}
                      Chaque type a des caractéristiques, des coûts et des avantages différents. L'assurance vie temporaire est souvent plus abordable, tandis que les polices vie entière et universelle offrent des fonctionnalités supplémentaires mais s'accompagnent de primes plus élevées.
                    </>
                  ),
                },
                {
                  title: "Évaluez votre budget",
                  desc: (
                    <>
                      Établissez un budget pour les primes d'assurance vie. Déterminez combien vous pouvez raisonnablement vous permettre de payer chaque mois ou chaque année pour votre couverture. Soyez réaliste quant à votre situation financière pour éviter les engagements excessifs.
                    </>
                  ),
                },
                {
                  title: "Comparez les devis de plusieurs fournisseurs",
                  desc: (
                    <>
                      Obtenez des devis de différents assureurs pour comparer les primes et les options de couverture. Les outils en ligne et les courtiers d'assurance peuvent vous aider à comparer facilement les devis de différentes compagnies. Recherchez des assureurs réputés avec des avis clients positifs.
                    </>
                  ),
                },
                {
                  title: "Envisagez l'assurance vie temporaire",
                  desc: (
                    <>
                      Si votre objectif principal est d'obtenir une couverture abordable, envisagez l'assurance vie temporaire. Elle offre une couverture pour une période déterminée (p. ex., 10, 20 ou 30 ans) et s'accompagne souvent de primes moins élevées que l'assurance vie permanente.
                    </>
                  ),
                },
                {
                  title: "Optez pour un mode de vie sain",
                  desc: (
                    <>
                      Les primes d'assurance sont souvent influencées par votre santé et votre mode de vie. Les non-fumeurs, les personnes avec un IMC sain et celles sans problèmes de santé significatifs bénéficient généralement de primes plus basses. Maintenez un mode de vie sain pour potentiellement bénéficier de meilleurs tarifs.
                    </>
                  ),
                },
                {
                  title: "Révisez et mettez à jour votre police régulièrement",
                  desc: (
                    <>
                      Révisez périodiquement votre police d'assurance vie pour vous assurer qu'elle correspond à vos besoins actuels et à votre situation financière. Les changements de vie, comme le mariage, la naissance d'un enfant ou des changements de revenus, peuvent nécessiter des ajustements à votre couverture.
                    </>
                  ),
                },
                {
                  title: "Consultez un professionnel de l'assurance chez einsured.ca",
                  desc: (
                    <>
                      Si vous n'êtes pas sûr de la meilleure police pour votre situation, envisagez de consulter un professionnel de l'assurance ou un conseiller financier chez{" "}
                      <span className="font-semibold text-sky-500">
                        einsured.ca
                      </span>
                      . Ils peuvent vous fournir des conseils personnalisés en fonction de vos besoins et circonstances spécifiques.
                    </>
                  ),
                },
              ].map((step, idx) => (
                <li key={idx} className="flex items-start space-x-3">
                  <CheckCircle
                    aria-hidden="true"
                    className="h-5 w-5 text-sky-500 mt-1 shrink-0"
                    strokeWidth={2.5}
                    fill="none"
                  />
                  <span>
                    <strong className="text-[#49628b]">{step.title} :</strong>{" "}
                    {step.desc}
                  </span>
                </li>
              ))}
            </ul>

            <p className="text-base sm:text-lg text-gray-700 mt-10 sm:mt-12 leading-relaxed">
              N'oubliez pas que la clé d'une assurance vie abordable est de trouver le bon équilibre entre couverture et coût. En prenant le temps de rechercher et de comprendre vos options, vous pouvez souscrire une police qui offre la protection dont vous avez besoin sans mettre à rude épreuve votre budget.
            </p>

            <div className="flex justify-center mt-10">
              <button
                onClick={() =>
                  window.open(
                    "https://book.einsured.ca/widget/bookings/ei-team",
                    "_blank",
                    "noopener,noreferrer",
                  )
                }
                className="bg-sky-500 text-white text-base sm:text-lg font-semibold px-8 sm:px-10 py-3 rounded-full hover:bg-sky-600 transition w-full sm:w-auto"
              >
                Parler à un conseiller !
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* What if a Claim Arises */}
      <section className="py-16 sm:py-20 px-4 sm:px-6 md:px-24 bg-[#e8eff8] text-gray-800 relative border-t-4 border-[#1e48a0]">
        <div className={`${siteContainer}`}>
          <div className="max-w-5xl mx-auto text-center mb-8 sm:mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#49628b] mb-3 sm:mb-4">
              Que faire en cas de réclamation ?
            </h2>
            <p className="text-base sm:text-lg text-gray-700">
              Soumettre une réclamation d'assurance vie est un processus crucial qui implique d'aviser la compagnie d'assurance et de fournir les documents nécessaires après le décès de l'assuré.
            </p>
          </div>

          <div className="max-w-5xl mx-auto">
            <h3 className="text-xl sm:text-2xl font-bold text-[#49628b] mb-6 sm:mb-10 text-center">
              Voici un guide étape par étape sur la façon de soumettre une réclamation d'assurance vie :
            </h3>

            <ul className="space-y-5 sm:space-y-6 text-base sm:text-lg leading-relaxed">
              {[
                {
                  title: "Avisez la compagnie d'assurance :",
                  text: "Contactez la compagnie d'assurance dès que possible après le décès de l'assuré. Obtenez les formulaires de réclamation de la compagnie et renseignez-vous sur les exigences spécifiques.",
                },
                {
                  title: "Rassemblez la documentation :",
                  text: "Recueillez les documents nécessaires, notamment l'acte de décès. L'acte de décès est un document essentiel et est généralement requis par l'assureur pour traiter la réclamation. Ayez une copie de la police d'assurance et tout autre document pertinent.",
                },
                {
                  title: "Remplissez les formulaires de réclamation :",
                  text: "Remplissez les formulaires de réclamation requis fournis par la compagnie d'assurance de manière précise et complète. Incluez tous les détails nécessaires, tels que le numéro de police, la cause du décès et les informations sur les bénéficiaires.",
                },
                {
                  title: "Soumettez les documents requis :",
                  text: "Avec les formulaires de réclamation, soumettez l'acte de décès et tout autre document demandé par la compagnie d'assurance. Des documents supplémentaires peuvent inclure une preuve d'identité du demandeur, le document de police et tout dossier médical si requis.",
                },
                {
                  title: "Vérification des bénéficiaires :",
                  text: "Assurez-vous que les bénéficiaires nommés sont correctement identifiés dans les formulaires de réclamation. Les bénéficiaires peuvent avoir besoin de fournir une preuve de leur identité et de leur lien avec l'assuré.",
                },
                {
                  title: "Attendez le traitement :",
                  text: "Une fois que vous avez soumis les formulaires de réclamation et les documents requis, la compagnie d'assurance lancera le traitement des réclamations. Le délai de traitement peut varier, mais la compagnie d'assurance fournira généralement un calendrier estimé.",
                },
                {
                  title: "Décision sur la réclamation :",
                  text: "La compagnie d'assurance examinera la réclamation et prendra une décision pour l'approuver ou la refuser. Si elle est approuvée, l'assureur déterminera le montant de la prestation de décès.",
                },
                {
                  title: "Paiement de la prestation de décès :",
                  text: "Si la réclamation est approuvée, la compagnie d'assurance versera la prestation de décès aux bénéficiaires. Le paiement peut être effectué en une somme forfaitaire ou en versements, selon les modalités de la police et les préférences des bénéficiaires.",
                },
                {
                  title: "Suivi :",
                  text: "En cas de retards ou de questions pendant le processus de réclamation, faites un suivi auprès de la compagnie d'assurance pour obtenir des mises à jour. Conservez un dossier de toutes les communications avec l'assureur.",
                },
                {
                  title: "Consultez des professionnels :",
                  text: "Si vous rencontrez des difficultés ou avez des préoccupations lors du processus de réclamation, envisagez de demander l'aide d'un professionnel juridique ou financier. Il est essentiel d'aborder le processus de réclamation d'assurance vie avec soin et attention aux détails.",
                },
              ].map((item, i) => (
                <li key={i} className="flex items-start space-x-3">
                  <CheckCircle
                    aria-hidden="true"
                    className="h-5 w-5 text-sky-500 mt-[2px] shrink-0"
                    strokeWidth={2.5}
                    fill="none"
                  />
                  <span>
                    <strong className="text-[#49628b]">{item.title}</strong>{" "}
                    {item.text}
                  </span>
                </li>
              ))}
            </ul>

            <p className="text-base sm:text-lg text-gray-700 mt-8 sm:mt-10 leading-relaxed">
              La communication rapide et la documentation précise sont des facteurs clés pour assurer une expérience de réclamation fluide et efficace pendant ce qui est sans aucun doute une période difficile.
            </p>

            <div className="flex justify-center mt-10 sm:mt-12">
              <button
                onClick={() =>
                  window.open(
                    "https://book.einsured.ca/widget/bookings/ei-team",
                    "_blank",
                    "noopener,noreferrer",
                  )
                }
                className="border-2 border-sky-500 text-sky-600 text-base sm:text-lg font-semibold px-8 sm:px-10 py-3 rounded-full hover:bg-sky-500 hover:text-white transition w-full sm:w-auto"
              >
                Obtenez les meilleurs services !
              </button>
            </div>
          </div>

          <div className="absolute bottom-0 left-0 w-full h-[4px] bg-[#1e48a0]"></div>
        </div>
      </section>

      {/* Why einsured.ca */}
      <section className="py-16 sm:py-20 px-4 sm:px-6 md:px-24 bg-white text-gray-800">
        <div className={`${siteContainer}`}>
          <div className="max-w-5xl mx-auto text-center mb-8 sm:mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#49628b] mb-4 sm:mb-8">
              Pourquoi einsured.ca ?
            </h2>
          </div>

          <div className="max-w-4xl mx-auto text-base sm:text-lg leading-relaxed space-y-6 text-justify">
            <p>
              Chez{" "}
              <Link
                to="/"
                className="text-[#20a7df] font-semibold hover:underline"
              >
                einsured.ca
              </Link>
              , nous sommes fiers d'être l'un des principaux fournisseurs canadiens de tarifs d'assurance vie en ligne. Notre équipe hautement expérimentée d'experts canadiens en assurance vie va au-delà des attentes pour vous fournir rapidement plusieurs soumissions d'assurance vie, vous assurant de recevoir les meilleures options possibles. Nous prenons en compte vos objectifs financiers uniques, votre budget et vos plans d'avenir pour vous aider à sélectionner la police parfaite qui répond à tous vos besoins.
            </p>

            <p>
              Notre courtage offre uniquement des produits de la plus haute qualité, et nous nous engageons à fournir un service client exceptionnel qui nous distingue comme l'un des premiers fournisseurs d'assurance vie au Canada. Notre équipe offre également des conseils d'assurance pertinents qui vous aideront à prendre des décisions éclairées et à fournir des conseils précieux lorsque vous planifiez votre police conformément à vos attentes.
            </p>

            <p>
              Chez{" "}
              <Link
                to="/"
                className="text-[#20a7df] font-semibold hover:underline"
              >
                einsured.ca
              </Link>
              , nous restons fermes dans notre engagement à offrir des polices d'assurance vie exceptionnelles, et ce dévouement indéfectible nous a aidés à consolider notre position de marque leader au Canada.
            </p>

            <p>
              En nous choisissant comme votre{" "}
              <span className="text-[#20a7df] font-semibold">
                fournisseur d'assurance vie
              </span>
              , vous bénéficierez de conseils d'experts qui vous aideront à planifier et à atteindre vos objectifs, offrant tranquillité d'esprit et sécurité financière pour vous et vos proches.
            </p>
          </div>

          <div className="flex justify-center mt-10 sm:mt-12">
            <button
              onClick={() =>
                window.open(
                  "https://book.einsured.ca/widget/bookings/ei-team",
                  "_blank",
                  "noopener,noreferrer",
                )
              }
              className="bg-sky-500 text-white text-base sm:text-lg font-semibold px-8 sm:px-10 py-3 rounded-full hover:bg-sky-600 transition w-full sm:w-auto"
            >
              Réservez une réunion gratuite !
            </button>
          </div>
        </div>
      </section>

      {/* Frequently Asked Questions */}
      <section className="py-16 sm:py-20 px-4 sm:px-6 md:px-24 bg-white text-gray-800">
        <div className={`${siteContainer}`}>
          <div className="max-w-5xl mx-auto text-center mb-8 sm:mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#49628b]">
              Foire aux questions sur l'assurance vie
            </h2>
          </div>

          <div className="max-w-4xl mx-auto space-y-4">
            {faqs.map((faq, index) => {
              const expanded = activeIndex === index;
              const panelId = `faq-panel-${index}`;
              const btnId = `faq-btn-${index}`;
              return (
                <div
                  key={index}
                  className="border rounded-lg shadow-sm transition-all bg-white"
                >
                  <button
                    id={btnId}
                    className="w-full flex justify-between items-center text-left px-4 sm:px-6 py-3.5 sm:py-4 font-semibold text-base sm:text-lg text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1e48a0] focus-visible:ring-offset-2"
                    onClick={() => toggleFAQ(index)}
                    aria-expanded={expanded}
                    aria-controls={panelId}
                  >
                    {faq.question}
                    {expanded ? (
                      <Minus
                        className="w-6 h-6 text-gray-700"
                        aria-hidden="true"
                      />
                    ) : (
                      <Plus
                        className="w-6 h-6 text-gray-700"
                        aria-hidden="true"
                      />
                    )}
                  </button>

                  {expanded && (
                    <div
                      id={panelId}
                      role="region"
                      aria-labelledby={btnId}
                      className="px-4 sm:px-6 pb-4 text-gray-700 text-sm sm:text-base"
                    >
                      {faq.answer}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <BlogSection title="Derniers articles" mode="related" relatedCount={3} />

      {/* Services Section */}
      <section className="py-12 sm:py-16 md:py-18 bg-white">
        <div className={siteContainer}>
          <div className="max-w-5xl mx-auto text-gray-800">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#49628b] text-center mb-10">
              Régions desservies
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 sm:gap-8">
              {[
                ["/fr/assurance-vie-brampton", "Assurance vie à Brampton"],
                ["/fr/assurance-vie-calgary", "Assurance vie à Calgary"],
                ["/fr/assurance-vie-mississauga", "Assurance vie à Mississauga"],
                ["/fr/assurance-vie-winnipeg", "Assurance vie à Winnipeg"],
                ["/fr/assurance-vie-toronto", "Assurance vie à Toronto"],
              ].map(([to, label]) => (
                <Link
                  key={to}
                  to={to}
                  className="group block rounded-[2rem] border border-[#d7e4f2] bg-[#e4edf8] p-6 sm:p-8 text-center shadow-md transition hover:-translate-y-1 hover:shadow-xl"
                >
                  <h3 className="text-xl sm:text-2xl font-semibold text-[#49628b] mb-4">
                    {label}
                  </h3>

                  <span className="inline-block bg-sky-400 group-hover:bg-sky-500 text-sky-950 font-semibold text-base sm:text-lg px-8 py-3 rounded-full shadow-md transition">
                    Voir le service
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default LifeInsurance;
